import { useEffect, useMemo, useState } from "react";
import { Control, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove, UseFormReturn } from "react-hook-form";
import { PageType, PreDiscountModal, VisitorFormData } from "src/api/visitor/visitor-types";

import { useLocation, useParams } from "react-router-dom";
import { useApiOperation } from "src/api/hooks";
import { getScheduledMessagesAsync } from "src/api/messages/messages-api";
import { Messages } from "src/api/messages/messages-type";

import clsx from "clsx";
import { useRecoilValue } from "recoil";
import { ContractOperationProductManage } from "src/api/contract/contract-types";
import { BaseButton, BaseTooltip } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import SpaceBaseTable from "src/pages/product/forms/commonSpaceForm/components/SpaceBaseTable";
import { useModal } from "src/recoil/modalState/hook";
import { globalPartnerState } from "src/recoil/partners/atom";
import { BULLET, isMobilePath } from "src/utils";
import * as xlsx from "xlsx";
import useVisitor from "../hooks/useVisitor";
import {
  mobileVisitorDetailColumns,
  visitorAddWithoutParkingColumns,
  visitorAddWithParkingColumns,
  visitorDetailWithoutParkingColumns,
  visitorDetailWithParkingColumns,
  visitorOrganizationColumn,
} from "./columns";
import ExcelUploadModal from "./ExcelUploadModal";

// 방문자 정보를 관리하는 컴포넌트
const VisitorInfo = ({
  pageType, // 페이지 타입 (add, detail 등)
  contractDetailInfo, // 계약 상세 정보
  control, // react-hook-form의 control 객체
  formVisitorInfoList, // 방문자 정보 리스트
  append, // 방문자 정보 추가 함수
  remove, // 방문자 정보 삭제 함수
  useFormReturn, // react-hook-form의 form 객체
  isExceedVisitorLimit, // 초대 가능 인원 초과 상태
}: {
  pageType: PageType;
  contractDetailInfo?: ContractOperationProductManage;
  control: Control<VisitorFormData>;
  formVisitorInfoList: FieldArrayWithId<VisitorFormData, "visitorInfoList", "id">[];
  append: UseFieldArrayAppend<VisitorFormData, "visitorInfoList">;
  remove: UseFieldArrayRemove;
  useFormReturn: UseFormReturn<VisitorFormData>;
  isExceedVisitorLimit?: boolean;
}) => {
  // 모달 상태 관리
  const { setBaseModal, setAbstractModalZ1 } = useModal();
  // 파트너 권한 확인
  const { isAuthority } = usePartnerAuthority();
  // URL 파라미터에서 방문 신청 번호와 계약 신청 번호 가져오기
  const { visitApplyNumber, contractApplyNumber } = useParams();
  // 방문자 정보 관련 훅
  const { contractId, visitorGroup } = useVisitor({ visitApplyNumber, contractApplyNumber });
  const isSelfRegist = visitorGroup?.visitType === "SELF_VISIT_REGIST";
  const location = useLocation();
  // 주차 할인 정보 상태
  const [preDiscountList, setPreDiscountList] = useState<PreDiscountModal[]>();
  // 예약된 메시지 상태
  const [scheduledMessages, setScheduledMessages] = useState<Messages[]>();

  // 방문자 추가 버튼 클릭 핸들러
  const handleClickAddButton = () => {
    append({ contractVisitorId: 0, invitationType: "phone" });
  };
  const partner = useRecoilValue(globalPartnerState);
  const isParkingFeatureActive = partner?.features
    ?.filter((feature) => {
      const parkingFeature = ["FEATURE_PARKING_KPS", "FEATURE_PARKING_IPARKING", "FEATURE_PARKING_AMANO"];
      return parkingFeature.includes(feature.name);
    })
    .some((feature) => feature.active);

  const csnKeyAccessors = ["visitorKeyCardButton", "visitorKeyCard"];
  const hasBasicRead = isAuthority("r", "BASIC");

  // 페이지 타입과 권한에 따른 컬럼 설정
  const newVisitorColumns = useMemo(() => {
    let cols: any[] = [];
    if (pageType === "add") {
      cols = isParkingFeatureActive ? visitorAddWithParkingColumns : visitorAddWithoutParkingColumns;
    } else if (isAuthority("d")) {
      cols = isParkingFeatureActive ? visitorDetailWithParkingColumns : visitorDetailWithoutParkingColumns;
    } else {
      cols = isParkingFeatureActive
        ? visitorDetailWithParkingColumns.filter((column) => column.accessor !== "delete")
        : visitorDetailWithoutParkingColumns.filter((column) => column.accessor !== "delete");
    }

    if (!hasBasicRead) cols = cols.filter((column) => !csnKeyAccessors.includes(column.accessor));

    if (pageType !== "add" && isSelfRegist) {
      const emailIdx = cols.findIndex((column) => column.accessor === "visitorEmail");
      const insertAt = emailIdx >= 0 ? emailIdx + 1 : cols.length;
      cols = [...cols.slice(0, insertAt), visitorOrganizationColumn, ...cols.slice(insertAt)];
    }

    return cols;
  }, [pageType, isAuthority, hasBasicRead, isSelfRegist]);

  // 페이지 타입과 권한에 따른 컬럼 설정
  const newMobileVisitorColumns = useMemo(() => {
    let cols = isAuthority("d") ? mobileVisitorDetailColumns : mobileVisitorDetailColumns.filter((column) => column.accessor !== "delete");

    if (!hasBasicRead) cols = cols.filter((column) => !csnKeyAccessors.includes(column.accessor));

    if (isSelfRegist) {
      const emailIdx = cols.findIndex((column) => column.accessor === "visitorEmail");
      const insertAt = emailIdx >= 0 ? emailIdx + 1 : cols.length;
      cols = [...cols.slice(0, insertAt), visitorOrganizationColumn, ...cols.slice(insertAt)];
    }

    return cols;
  }, [pageType, isAuthority, hasBasicRead, isSelfRegist]);

  // API 호출 훅
  // const { executeAsync: getVisitorParkingPreDiscountList } = useApiOperation(getVisitorParkingPreDiscountListAsync);
  const { executeAsync: getScheduledMessages } = useApiOperation(getScheduledMessagesAsync);

  // 주차 할인 정보 조회
  // useEffect(() => {
  //   const fetchApi = async () => {
  //     if (formVisitorInfoList && formVisitorInfoList[0]?.preDiscountId && contractId) {
  //       if (String(formVisitorInfoList[0].preDiscountId) === "0") return;

  //       const preDiscountIds = formVisitorInfoList.map((data) => data.preDiscountId).join(",");

  //       const getVisitorParkingPreDiscountListData = await getVisitorParkingPreDiscountList({
  //         contractId: String(contractId),
  //         preDiscountIds: preDiscountIds,
  //       }).then((data) => data?.data?.data?.preDiscount);

  //       setPreDiscountList(getVisitorParkingPreDiscountListData);
  //     }
  //   };

  //   fetchApi();
  // }, [formVisitorInfoList, contractId]);

  // 예약된 메시지 조회
  // https://pnpt.atlassian.net/wiki/spaces/dsplan/pages/989430465/Ctrl.room+bulk+Ctrl.R+_v1.25#%EB%B0%A9%EB%AC%B8%EC%9E%90-%EC%B4%88%EB%8C%80-%3E-%EC%83%81%EC%84%B8-%ED%99%94%EB%A9%B4
  useEffect(() => {
    const fetchApi = async () => {
      // 등록 페이지에서는 사용하지 않음
      if (pageType === "add") return;

      // 방문자 정보가 없으면 종료
      if (!formVisitorInfoList || formVisitorInfoList.length === 0) return;

      // 휴대폰 번호가 있는 방문자 필터링
      const requestPhoneDataList = formVisitorInfoList
        .filter((element) => element.visitorMobileNumber)
        .map((element) => {
          return {
            serviceId: Number(element.contractVisitorId),
            serviceType: "SERVICE_CONTRACT_VISITOR",
          };
        });

      // 배열을 chunk 단위로 나누는 함수
      const chunkArray = (array: any[], size: number) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
          chunks.push(array.slice(i, i + size));
        }
        return chunks;
      };

      // 각 유형별로 예약된 메시지 조회
      if (requestPhoneDataList.length > 0) {
        // 50개씩 나누어 요청
        const phoneDataChunks = chunkArray(requestPhoneDataList, 30);
        for (const chunk of phoneDataChunks) {
          const getScheduledMessagesData = await getScheduledMessages({
            page: 0,
            size: 9999,
            messageType: "MESSAGE_KAKAOTALK",
            eventType: "CT_VISITOR_KTP015",
            contentsList: chunk,
          }).then((data) => data?.data?.data?.content);

          setScheduledMessages((prev) => [...(prev || []), ...(getScheduledMessagesData || [])]);
        }
      }

      // 영어 이메일이 있는 방문자 필터링
      const requestEnEmailDataList = formVisitorInfoList
        .filter((element) => element.visitorEmail && element.visitorEmailLang === "en")
        .map((element) => {
          return {
            serviceId: Number(element.contractVisitorId),
            serviceType: "SERVICE_CONTRACT_VISITOR",
          };
        });

      if (requestEnEmailDataList.length > 0) {
        // 50개씩 나누어 요청
        const enEmailDataChunks = chunkArray(requestEnEmailDataList, 30);
        for (const chunk of enEmailDataChunks) {
          const getScheduledMessagesData = await getScheduledMessages({
            page: 0,
            size: 9999,
            messageType: "MESSAGE_EMAIL",
            eventType: "CT_VISITOR_MVT001",
            contentsList: chunk,
          }).then((data) => data?.data?.data?.content);
          setScheduledMessages((prev) => [...(prev || []), ...(getScheduledMessagesData || [])]);
        }
      }

      // 한국어 이메일이 있는 방문자 필터링
      const requestKoEmailDataList = formVisitorInfoList
        .filter((element) => element.visitorEmail && element.visitorEmailLang === "ko")
        .map((element) => {
          return {
            serviceId: Number(element.contractVisitorId),
            serviceType: "SERVICE_CONTRACT_VISITOR",
          };
        });

      if (requestKoEmailDataList.length > 0) {
        // 50개씩 나누어 요청
        const koEmailDataChunks = chunkArray(requestKoEmailDataList, 30);
        for (const chunk of koEmailDataChunks) {
          const getScheduledMessagesData = await getScheduledMessages({
            page: 0,
            size: 9999,
            messageType: "MESSAGE_EMAIL",
            eventType: "CT_VISITOR_MVT002",
            contentsList: chunk,
          }).then((data) => data?.data?.data?.content);
          setScheduledMessages((prev) => [...(prev || []), ...(getScheduledMessagesData || [])]);
        }
      }
    };

    fetchApi();
  }, [formVisitorInfoList]);

  /**
   *  모달에서 사용할 엑셀 파일 처리 핸들러(모달 확인버튼 클릭시)
   *  엑셀 파일을 읽어서 방문자 정보 추가
   * blobUrl : 엑셀 파일 핸들링시 클라이언트에서 사용
   * blobUrl 없을 경우 모달 표시
   * @param blobUrl
   * @returns
   */
  const handleClickExcelLoad = async (blobUrl?: string) => {
    try {
      if (!blobUrl) {
        return setBaseModal({
          isOpen: true,
          title: "오류 발생",
          btnRightTitle: "확인",
          children: "엑셀 파일을 처리하는 중 오류가 발생했습니다.",
        });
      }

      const response = await fetch(blobUrl);
      const buffer = await response.arrayBuffer();
      const workbook = xlsx.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // 엑셀 데이터 범위 설정
      const range = xlsx.utils.decode_range(worksheet["!ref"] || "");

      // B7의 위치
      // const startRow = 6; // 0-indexed → 7번째 행 = index 6
      const startRow = 7; // 0-indexed → 8번째 행 = index 7
      const startCol = 1; // B열 = index 1

      // 마지막 행/열까지 포함한 범위로 재설정
      const newRange = {
        s: { r: startRow, c: startCol },
        e: { r: range.e.r, c: range.e.c },
      };

      const rangeStr = xlsx.utils.encode_range(newRange);

      const json = xlsx.utils.sheet_to_json(worksheet, {
        // 엑셀 열 매핑 (B열부터 시작)
        header: ["visitorName", "invitationType", "phoneOrEmail", "visitorEmailLang", "visitorCarNumber"],
        // 5번째 행(예시 데이터)부터 B열에서 시작
        range: rangeStr,
        // 원본 값 그대로 사용 (날짜나 숫자를 변환하지 않음)
        raw: true,
        // 빈 행 포함
        blankrows: false,
      });

      // 유효성 검사
      const errorCaseList: string[] = [];

      // 최대 방문자 수 체크
      if (formVisitorInfoList.length + json.length > 100) {
        errorCaseList.push("방문자는 100명을 초과할 수 없습니다.");
      }

      if (json === undefined || json.length === 0) {
        errorCaseList.push("추가할 방문자가 없습니다. ");
      }

      // 각 방문자 정보 유효성 검사
      json.forEach((obj: any) => {
        // 에러 케이스 예외 처리

        if (
          !obj.invitationType ||
          !(
            obj.invitationType?.toString().replaceAll(" ", "")?.toLowerCase() === "phone" ||
            obj.invitationType?.toString().replaceAll(" ", "")?.toLowerCase() === "email"
          )
        ) {
          // 휴대폰, 이메일 입력란이 공란인 경우 : C열을 확인하세요.
          // 휴대폰, 이메일 입력란에 오타가 있는 경우 : C열을 확인하세요.

          errorCaseList.push("C열을 확인하세요.");
        }

        if (obj.visitorEmailLang) {
          // 이메일 언어 유효성 검사
          // 이메일 언어가 있을떄 오타 검사
          if (
            !(
              obj.visitorEmailLang?.toString().replaceAll(" ", "")?.toLowerCase() === "ko" ||
              obj.visitorEmailLang?.toString().replaceAll(" ", "")?.toLowerCase() === "en"
            )
          ) {
            // 값을 입력 했는데 ko 도 아니고 en도 아닌경우
            // 이메일 언어 입력란에 오타가 있는 경우 : E열을 확인하세요.
            errorCaseList.push("E열을 확인하세요.");
          }
        }
      });

      const newErrorCaseList = [...new Set(errorCaseList)];

      // 에러 발생 시 모달 표시
      if (errorCaseList.length > 0) {
        return setBaseModal({
          isOpen: true,
          title: "파일을 업로드할 수 없습니다.",
          btnRightTitle: "확인",
          children: (
            <div style={{ wordBreak: "break-all" }}>
              <div className="font14 text-primary3 pre-formatted keep-all">{"아래의 내용을 확인 후 다시 시도해주세요."} </div>
              {/* 에러 리스트 전달하면 불릿으로 표시 */}
              <div className="w-100 text-left py10 px15 mt20 overflow-scroll text-primary" style={{ backgroundColor: "#F2F4F6", maxHeight: 250 }}>
                <ul style={{ listStyle: "none" }}>
                  {newErrorCaseList.map((item, idx) => (
                    //마지막 리스트는 마진 없음
                    <li key={idx} className={clsx({ mb5: idx !== newErrorCaseList.length - 1 })}>
                      • {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ), // 중복제거
        });
      }

      // 위애서 유효성 검사 통과 시 방문자 정보 추가
      json.forEach((obj: any) => {
        const visitorInfo: VisitorFormData["visitorInfoList"][0] = {
          contractVisitorId: 0,
          visitorName: obj.visitorName?.toString() || "", //방문자 이름은 공백이 있다면 유지
          invitationType: obj.invitationType?.toString()?.replaceAll(" ", "")?.toLowerCase() || "",
          visitorEmail:
            obj.invitationType?.toString().replaceAll(" ", "")?.toLowerCase() === "email" ? obj?.phoneOrEmail?.toString().replaceAll(" ", "") : "",
          visitorMobileNumber:
            obj.invitationType?.toString().replaceAll(" ", "")?.toLowerCase() === "phone" ? obj?.phoneOrEmail?.toString().replaceAll(" ", "") : "",
          visitorEmailLang: obj.visitorEmailLang?.toString()?.replaceAll(" ", "")?.toLowerCase() || "en",
          visitorCarNumber: obj.visitorCarNumber?.toString()?.replaceAll(" ", "") || "",
        };

        append(visitorInfo);
      });

      setAbstractModalZ1({ isOpen: false });
    } catch (error) {
      console.error("엑셀 파일 처리 중 오류 발생:", error);
      setBaseModal({
        isOpen: true,
        title: "오류 발생",
        btnRightTitle: "확인",
        children: "엑셀 파일을 처리하는 중 오류가 발생했습니다.",
      });
    }
  };

  console.log(contractDetailInfo);
  return (
    <article className="contents-container__wrap-article mobile-table">
      {/* 방문자 정보 섹션 헤더 */}
      <BaseSectionTitle
        title={"방문자 정보"}
        leftArea={
          !contractDetailInfo || contractDetailInfo?.isVisitorUnlimited ? null : (
            <div className="gray-chip ml10">
              <p className={isExceedVisitorLimit ? "text-red" : ""}>
                남은 초대 가능 인원 :
                {contractDetailInfo?.visitorMaxNums !== undefined && contractDetailInfo?.visitorNums !== undefined
                  ? contractDetailInfo?.visitorMaxNums - contractDetailInfo?.visitorNums
                  : 0}
                명
              </p>
              <BaseTooltip
                size={16}
                touchIcon={"QUESTION"}
                children={
                  <ul className="base-list">
                    <li className="text-primary font13">이미 초대한 인원 : {contractDetailInfo?.visitorNums}명</li>
                    <li className="text-primary font13">초대 한도 인원 : {contractDetailInfo?.visitorMaxNums}명</li>
                  </ul>
                }
              />
            </div>
          )
        }
        noHashLink={pageType === "add"}
        className="pb10"
        rightArea={
          isAuthority("w") &&
          pageType === "add" && (
            <div className="d-flex gap10">
              <BaseButton
                title="엑셀 업로드"
                disabled={formVisitorInfoList.length >= 100}
                className="color-white icon-excel"
                onClick={() =>
                  setAbstractModalZ1({ isOpen: true, size: "medium", children: <ExcelUploadModal handleClickExcelLoad={handleClickExcelLoad} /> })
                }
              />
              <BaseButton title="개별 추가" disabled={formVisitorInfoList.length >= 100} onClick={handleClickAddButton} />
            </div>
          )
        }
      />
      <div className="description flex-row flex-start-start pb20">
        <p className="text-primary3 font14">{BULLET} 한번에 최대 100명까지 등록가능합니다.</p>
      </div>

      {/* 방문자 정보 테이블 */}
      {formVisitorInfoList && (
        <div>
          <SpaceBaseTable
            data={formVisitorInfoList || []}
            columns={isMobilePath(location.pathname) ? newMobileVisitorColumns : newVisitorColumns}
            useFormReturn={useFormReturn}
            control={control}
            pageType={pageType}
            remove={remove}
            preDiscountList={preDiscountList}
            scheduledMessages={scheduledMessages}
            ScrollShadowProviderClassName=""
            className="max-height700"
            formVisitorInfoList={formVisitorInfoList}
          />
          {pageType === "add" && (
            <div className="pt20 flex-center-end">
              {/* 전체 삭제 버튼 등록페이지만 */}
              <BaseButton
                title="전체 삭제"
                disabled={formVisitorInfoList.length <= 0}
                className="color-white icon-trash"
                onClick={() => {
                  setBaseModal({
                    isOpen: true,
                    title: "입력한 방문자 정보를 전체 삭제하시겠습니까?",
                    btnRightTitle: "확인",
                    btnLeftTitle: "취소",

                    onClose: () => {
                      setBaseModal({ isOpen: false });
                    },
                    onClick: () => {
                      setBaseModal({ isOpen: false });
                      setTimeout(() => {
                        remove();
                        //setBaseModal 과 함께 사용시 비정상 동작으로 setTimeout 사용
                        //방문자 목록 모두 삭제
                      }, 0);
                    },
                  });
                }}
              />
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default VisitorInfo;
